<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <img src="https://www.dof.gov.ph/wp-content/uploads/2024/02/DOF.png" alt="DOF Logo" class="login-logo">
          <h1>Legislative Monitoring Portal</h1>
          <p>Department of Finance</p>
        </div>

        <!-- Step 1: Enter Email -->
        <div v-if="step === 'email'" class="login-form-container">
          <h2>Forgot Password</h2>
          <p class="subtitle">Enter your email address and we'll send you a link to reset your password.</p>
          <form @submit.prevent="sendResetLink" class="login-form">
            <div class="form-group">
              <label for="email">Email Address</label>
              <InputText 
                id="email" 
                v-model="email" 
                type="email" 
                placeholder="Enter your email"
                :class="{ 'p-invalid': errors.email }"
                required
              />
              <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
            </div>

            <div v-if="errors.general" class="error-message">
              <small class="p-error">{{ errors.general }}</small>
            </div>

            <Button 
              type="submit" 
              label="Send Reset Link" 
              class="login-button"
              :loading="loading"
              icon="pi pi-envelope"
            />
          </form>
          <div class="back-link">
            <router-link to="/login">← Back to Login</router-link>
          </div>
        </div>

        <!-- Step 2: Email Sent Confirmation -->
        <div v-if="step === 'sent'" class="login-form-container">
          <div class="success-icon">
            <i class="pi pi-check-circle"></i>
          </div>
          <h2>Check Your Email</h2>
          <p class="subtitle">If an account with <strong>{{ email }}</strong> exists, we've sent a password reset link. Please check your inbox.</p>
          <p class="subtitle" style="margin-top: 1rem; font-size: 0.85rem; color: #64748b;">The link will expire in 60 minutes.</p>
          <div class="back-link" style="margin-top: 1.5rem;">
            <router-link to="/login">← Back to Login</router-link>
          </div>
        </div>

        <!-- Step 3: Reset Password Form -->
        <div v-if="step === 'reset'" class="login-form-container">
          <h2>Set New Password</h2>
          <p class="subtitle">Enter your new password below.</p>
          <form @submit.prevent="resetPassword" class="login-form">
            <div class="form-group">
              <label for="new_password">New Password</label>
              <Password 
                id="new_password"
                v-model="newPassword" 
                placeholder="Enter new password"
                toggleMask
                :class="{ 'p-invalid': errors.password }"
                required
              />
              <small v-if="errors.password" class="p-error">{{ errors.password }}</small>
            </div>

            <div class="form-group">
              <label for="confirm_password">Confirm Password</label>
              <Password 
                id="confirm_password"
                v-model="confirmPassword" 
                placeholder="Confirm new password"
                :feedback="false"
                toggleMask
                :class="{ 'p-invalid': errors.password_confirmation }"
                required
              />
              <small v-if="errors.password_confirmation" class="p-error">{{ errors.password_confirmation }}</small>
            </div>

            <div v-if="errors.general" class="error-message">
              <small class="p-error">{{ errors.general }}</small>
            </div>

            <Button 
              type="submit" 
              label="Reset Password" 
              class="login-button"
              :loading="loading"
              icon="pi pi-lock"
            />
          </form>
        </div>

        <!-- Step 4: Password Reset Success -->
        <div v-if="step === 'done'" class="login-form-container">
          <div class="success-icon">
            <i class="pi pi-check-circle"></i>
          </div>
          <h2>Password Reset Successful</h2>
          <p class="subtitle">Your password has been updated. You can now sign in with your new password.</p>
          <router-link to="/login" class="login-button-link">
            <Button label="Go to Login" class="login-button" icon="pi pi-sign-in" />
          </router-link>
        </div>

        <!-- Invalid Token -->
        <div v-if="step === 'invalid'" class="login-form-container">
          <div class="error-icon">
            <i class="pi pi-times-circle"></i>
          </div>
          <h2>Invalid or Expired Link</h2>
          <p class="subtitle">This password reset link is invalid or has expired. Please request a new one.</p>
          <div class="back-link" style="margin-top: 1.5rem;">
            <a href="#" @click.prevent="step = 'email'; errors = {}">Request New Link</a>
          </div>
          <div class="back-link">
            <router-link to="/login">← Back to Login</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import api from '../axios.js'

export default {
  components: { InputText, Password, Button },
  data() {
    return {
      step: 'email',
      email: '',
      newPassword: '',
      confirmPassword: '',
      token: '',
      loading: false,
      errors: {}
    }
  },
  mounted() {
    // Check if we're coming from a reset link
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const email = urlParams.get('email')

    if (token && email) {
      this.token = token
      this.email = email
      this.verifyToken()
    }
  },
  methods: {
    async sendResetLink() {
      this.loading = true
      this.errors = {}

      if (!this.email) {
        this.errors.email = 'Email is required'
        this.loading = false
        return
      }

      try {
        await api.post('/forgot-password', { email: this.email })
        this.step = 'sent'
      } catch (error) {
        if (error.response?.data?.errors?.email) {
          this.errors.email = error.response.data.errors.email[0]
        } else {
          this.errors.general = 'Something went wrong. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    async verifyToken() {
      try {
        await api.get('/verify-reset-token', {
          params: { token: this.token, email: this.email }
        })
        this.step = 'reset'
      } catch (error) {
        this.step = 'invalid'
      }
    },

    async resetPassword() {
      this.loading = true
      this.errors = {}

      if (!this.newPassword) {
        this.errors.password = 'Password is required'
        this.loading = false
        return
      }

      if (this.newPassword.length < 8) {
        this.errors.password = 'Password must be at least 8 characters'
        this.loading = false
        return
      }

      if (this.newPassword !== this.confirmPassword) {
        this.errors.password_confirmation = 'Passwords do not match'
        this.loading = false
        return
      }

      try {
        await api.post('/reset-password', {
          token: this.token,
          email: this.email,
          password: this.newPassword,
          password_confirmation: this.confirmPassword
        })
        this.step = 'done'
      } catch (error) {
        if (error.response?.data?.message) {
          this.errors.general = error.response.data.message
        } else if (error.response?.data?.errors) {
          const errs = error.response.data.errors
          if (errs.password) this.errors.password = errs.password[0]
        } else {
          this.errors.general = 'Something went wrong. Please try again.'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.login-container {
  width: 100%;
  max-width: 400px;
}

.login-card {
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 20px 40px rgba(30, 58, 138, 0.3);
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.login-logo {
  height: 60px;
  margin-bottom: 1rem;
}

.login-header h1 {
  color: #1e3a8a;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.login-header p {
  color: #64748b;
  font-size: 0.9rem;
}

.login-form-container {
  padding-top: 0;
  margin-top: 0;
}

.login-form-container h2 {
  color: #1e3a8a;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
}

.subtitle {
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #1e3a8a;
  font-size: 0.9rem;
}

:deep(.p-inputtext),
:deep(.p-password input) {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
}

:deep(.p-inputtext:focus),
:deep(.p-password input:focus) {
  border-color: #1e3a8a;
  box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
}

:deep(.p-invalid) {
  border-color: #ef4444 !important;
}

.login-button {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;
  border: none !important;
  padding: 0.75rem !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  transition: all 0.3s !important;
  width: 100%;
}

.login-button:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(30, 58, 138, 0.3) !important;
}

.login-button-link {
  display: block;
  text-decoration: none;
  margin-top: 1.5rem;
}

.p-error {
  color: #ef4444;
  font-size: 0.8rem;
}

.error-message {
  text-align: center;
  padding: 0.5rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

.back-link {
  text-align: center;
  margin-top: 1rem;
}

.back-link a {
  color: #1e3a8a;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
}

.back-link a:hover {
  text-decoration: underline;
}

.success-icon {
  text-align: center;
  margin-bottom: 1rem;
}

.success-icon .pi {
  font-size: 3rem;
  color: #10b981;
}

.error-icon {
  text-align: center;
  margin-bottom: 1rem;
}

.error-icon .pi {
  font-size: 3rem;
  color: #ef4444;
}

@media (max-width: 480px) {
  .login-card {
    padding: 2rem;
  }
  
  .login-header h1 {
    font-size: 1.3rem;
  }
}
</style>
